import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, gql, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import ShiftSidebar from "./ShiftSidebar";
import ShiftsCalendarView from "./ShiftsCalendarView";
import ShiftListingView from "./ShiftListingView";
import EntityOperationBar from "../../components/EntityOperationBar";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { usePermissions } from "../../hooks/usePermissions";
import { buildListRoute, isBitSet, parseRouteEntityId } from "../../utils";
import { softDeleteData, restoreData } from "../../utils/softDelete";
import "../../starek.css";

const DAYS_OF_WEEK = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];

const formatTime = (sec) => {
    if (sec == null) return "-";
    const d = new Date(0);
    d.setSeconds(sec);
    let h = d.getUTCHours();
    const m = d.getUTCMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "pm" : "am";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
};

// UI Strings
const UI_STRINGS = {
    PAGE_TITLE: "Shifts",
    SEARCH_PLACEHOLDER: "Search",
    VIEW_MODE_TIMELINE: "Timeline View",
    VIEW_MODE_LIST: "List View",
    ENTITY_SINGULAR: "Shift",
    ENTITY_PLURAL: "Shifts",
    DELETE_TITLE_SINGLE: (name) => `Delete Shift ${name}?`,
    DELETE_TITLE_BULK: (count) => `Delete ${count} Shifts?`,
    DELETE_MESSAGE_SINGLE: (name) => `You are about to delete shift "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_MESSAGE_BULK: (
        count,
    ) => `You are about to delete ${count} shift${count !== 1 ? "s" : ""}.

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    TOTAL_SELECTED: (count) => `Total shifts selected: ${count}`,
    NOT_FOUND: "Shift not found",
};

const runsOnToDays = (mask) => {
    const days = [];
    DAYS_OF_WEEK.forEach((day, i) => {
        if (isBitSet(mask, i)) days.push(day.slice(0, 3));
    });
    return days.length ? days.join(", ") : "-";
};

const parseShiftData = (shifts) => {
    const dayShifts = DAYS_OF_WEEK.map((day) => ({ day, ranges: [] }));
    shifts.forEach((shift) => {
        const { start_time, end_time, runs_on } = shift;
        DAYS_OF_WEEK.forEach((_, index) => {
            if (isBitSet(runs_on, index)) {
                dayShifts[index].ranges.push({
                    shift_id: shift.id,
                    shift_name: shift.name,
                    start: start_time,
                    end: end_time,
                });
            }
        });
    });
    return dayShifts;
};

const GET_SHIFT_LIST_FOR_PLANT = gql`
    query GetShifts($where: ShiftWhereInput!) {
        shifts(where: $where) {
            id
            name
            description
            is_active
            deleted_at
            start_time
            end_time
            runs_on
            created_at
            updated_at
            created_by {
                id
                name
            }
            updated_by {
                id
                name
            }
        }
    }
`;

const UPDATE_MANY_SHIFT = gql`
    mutation UpdateManyShift($data: ShiftUpdateManyMutationInput!, $where: ShiftWhereInput) {
        updateManyShift(data: $data, where: $where) {
            count
        }
    }
`;

const DELETE_MANY_SHIFTS = gql`
    mutation DeleteManyShift($ids: [Int!]!) {
        deleteManyShift(where: { id: { in: $ids } }) {
            count
        }
    }
`;

const DELETE_ONE_SHIFT = gql`
    mutation DeleteOneShift($id: Int!) {
        deleteOneShift(where: { id: $id }) {
            id
        }
    }
`;

const CREATE_ONE_SHIFT = gql`
    mutation CreateOneShift($data: ShiftCreateInput!) {
        createOneShift(data: $data) {
            id
            name
            description
            start_time
            end_time
            runs_on
            created_at
        }
    }
`;

const UPDATE_ONE_SHIFT = gql`
    mutation UpdateOneShift(
        $data: ShiftUpdateInput!
        $where: ShiftWhereUniqueInput!
    ) {
        updateOneShift(data: $data, where: $where) {
            id
            name
            description
            start_time
            end_time
            runs_on
            updated_at
        }
    }
`;

const Shifts = () => {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const routeShiftId = parseRouteEntityId(routeId);
    const [searchParams, setSearchParams] = useSearchParams();

    const { can } = usePermissions();
    const canWrite = can("shift", "WRITE");
    const canDestroy = can("shift", "DESTROY");

    // View state lives in the URL so a refresh, a deep link or a back-navigation
    // lands on exactly the same list the user was looking at.
    const showInactive = searchParams.get("showInactive") === "1";

    const patchSearchParams = (patch, options = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") {
                next.delete(key);
            } else {
                next.set(key, value);
            }
        });
        setSearchParams(next, options);
    };

    const setShowInactive = (value, options = {}) =>
        patchSearchParams({ showInactive: value ? "1" : null }, options);

    const shiftQueryWhere = {
        plant_id: { equals: starekContext.lastUsedPlant.id },
        ...(showInactive ? {} : { is_active: { equals: true } }),
    };

    const { data, loading, error, refetch, client } = useQuery(
        GET_SHIFT_LIST_FOR_PLANT,
        {
            client: starekContext.engineClient,
            variables: { where: shiftQueryWhere },
        },
    );

    const [items, setItems] = useState([]);
    const [filterText, setFilterText] = useState("");
    const [viewMode, setViewMode] = useState("timeline");
    const [selectedRows, setSelectedRows] = useState([]);

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam && (viewParam === "timeline" || viewParam === "list")) {
            setViewMode(viewParam);
        }
    }, [searchParams]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [showAddShiftDrawer, setShowAddShiftDrawer] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [routeEditShift, setRouteEditShift] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    // Tracks the route id we already widened the query for, so the retry runs at most once.
    const inactiveRetryRef = useRef(null);
    const shiftsFromQuery = useMemo(() => data?.shifts ?? [], [data?.shifts]);

    const [deleteManyShifts] = useMutation(DELETE_MANY_SHIFTS, {
        client: starekContext.engineClient,
    });
    const [deleteOneShift] = useMutation(DELETE_ONE_SHIFT, {
        client: starekContext.engineClient,
    });
    const [updateManyShift] = useMutation(UPDATE_MANY_SHIFT, { client: starekContext.engineClient });
    const [togglingActiveId, setTogglingActiveId] = useState(null);
    const [createShift] = useMutation(CREATE_ONE_SHIFT, {
        client: starekContext.engineClient,
    });
    const [updateShift] = useMutation(UPDATE_ONE_SHIFT, {
        client: starekContext.engineClient,
    });

    useEffect(() => {
        if (data?.shifts) setItems(data.shifts);
    }, [data]);

    useEffect(() => {
        if (!routeId) {
            setRouteEditShift(null);
            return;
        }

        if (loading) return;

        const listRoute = buildListRoute("/shifts", searchParams);

        if (routeShiftId == null) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditShift(null);
            navigate(listRoute, { replace: true });
            return;
        }

        const shiftToEdit = shiftsFromQuery.find(
            (shift) => Number(shift?.id) === routeShiftId,
        );

        if (!shiftToEdit) {
            if (!showInactive && inactiveRetryRef.current !== routeShiftId) {
                inactiveRetryRef.current = routeShiftId;
                const widened = new URLSearchParams(searchParams);
                widened.set("showInactive", "1");
                setSearchParams(widened, { replace: true });
                return;
            }
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditShift(null);
            navigate(listRoute, { replace: true });
            return;
        }

        setRouteEditShift(shiftToEdit);
        setSelectedRows((current) =>
            current.length === 1 && Number(current[0]?.id) === routeShiftId
                ? current
                : [shiftToEdit],
        );
        setIsEditMode(true);
        setShowAddShiftDrawer(true);
    }, [
        routeId,
        routeShiftId,
        loading,
        shiftsFromQuery,
        showInactive,
        searchParams,
        setSearchParams,
        navigate,
        starekContext,
    ]);

    const filteredItems = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q),
        );
    }, [items, filterText]);

    const handleRowClicked = (row) => {
        navigate(buildListRoute(`/shifts/${row.id}`, searchParams));
    };

    const handleAdd = () => {
        setRouteEditShift(null);
        setIsEditMode(false);
        setShowAddShiftDrawer(true);
    };

    const handleEdit = () => {
        if (selectedRows.length !== 1) return;
        navigate(buildListRoute(`/shifts/${selectedRows[0].id}`, searchParams));
    };

    const handleDrawerClose = async () => {
        setShowAddShiftDrawer(false);
        setIsEditMode(false);
        setRouteEditShift(null);
        if (routeId) {
            navigate(buildListRoute("/shifts", searchParams), {
                replace: true,
            });
        }
        await refetch();
        setToggleCleared((p) => !p);
        setSelectedRows([]);
    };

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleSaveShift = async (shiftPayload) => {
        try {
            const currentUserId =
                starekContext?.currentUser?.id ||
                starekContext?.user?.id ||
                starekContext?.me?.id ||
                2;

            if (!shiftPayload.id) {
                // Create
                await createShift({
                    variables: {
                        data: {
                            name: shiftPayload.name,
                            description: shiftPayload.description || "",
                            start_time: shiftPayload.start_time,
                            end_time: shiftPayload.end_time,
                            runs_on: shiftPayload.runs_on,
                            plant: {
                                connect: {
                                    id: starekContext.lastUsedPlant.id,
                                },
                            },
                            created_by: {
                                connect: { id: Number(currentUserId) },
                            },
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });
            } else {
                // Update
                await updateShift({
                    variables: {
                        where: { id: shiftPayload.id },
                        data: {
                            name: { set: shiftPayload.name },
                            description: {
                                set: shiftPayload.description || "",
                            },
                            start_time: { set: shiftPayload.start_time },
                            end_time: { set: shiftPayload.end_time },
                            runs_on: { set: shiftPayload.runs_on },
                            updated_by: {
                                connect: { id: Number(currentUserId) },
                            },
                        },
                    },
                });
            }

            // The sidebar stays open to confirm the save and point at the next screen;
            // it closes when the user picks a next step or presses Done.
            await refetch();
            starekContext.showSuccess(
                shiftPayload.id
                    ? `Shift "${shiftPayload.name}" updated successfully`
                    : `Shift "${shiftPayload.name}" created successfully`,
            );
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    const handleDeleteShift = async (shift) => {
        try {
            await deleteOneShift({
                variables: { id: shift.id },
            });
            await handleDrawerClose();
            starekContext.showSuccess(
                `Shift "${shift.name}" deleted successfully`,
            );
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    const handleDelete = (shifts) => {
        if (!shifts || shifts.length === 0 || isDeleting) return;

        const isSingle = shifts.length === 1;
        const title = isSingle
            ? UI_STRINGS.DELETE_TITLE_SINGLE(shifts[0].name)
            : UI_STRINGS.DELETE_TITLE_BULK(shifts.length);

        const message = isSingle
            ? UI_STRINGS.DELETE_MESSAGE_SINGLE(shifts[0].name)
            : UI_STRINGS.DELETE_MESSAGE_BULK(shifts.length);

        openConfirm({
            title: title,
            content: (
                <>
                    <div
                        className="text-dark"
                        style={{ whiteSpace: "pre-wrap" }}
                    >
                        {message}
                    </div>
                    <p className="mt-4 text-dark fw-bold">
                        {UI_STRINGS.TOTAL_SELECTED(shifts.length)}
                        &nbsp;
                        <span className="badge bg-info">{shifts.length}</span>
                    </p>
                </>
            ),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            type: "danger",
            onConfirm: async () => {
                setIsDeleting(true);
                setDeleteDone(0);
                const ids = shifts.map((r) => r.id);

                try {
                    await deleteManyShifts({ variables: { ids } });
                    setDeleteDone(ids.length);
                    starekContext.showSuccess(
                        `Successfully deleted ${ids.length} shift(s)`,
                    );
                } catch (_) {
                    for (const id of ids) {
                        try {
                            await deleteOneShift({ variables: { id } });
                            setDeleteDone((d) => d + 1);
                        } catch (err) {
                            starekContext.showError(err);
                            break;
                        }
                    }
                } finally {
                    setIsDeleting(false);
                    setToggleCleared((p) => !p);
                    setSelectedRows([]);
                    await refetch();
                    closeConfirm();
                }
            },
            onClose: closeConfirm,
        });
    };

    const handleToggleActive = async (shifts, makeActive) => {
        const userId = starekContext?.currentUser?.id ?? starekContext?.user?.id;
        const ids = shifts.map((s) => s.id);
        try {
            await updateManyShift({
                variables: {
                    data: makeActive ? restoreData() : softDeleteData(),
                    where: { id: { in: ids } },
                },
            });
            starekContext.showSuccess(makeActive ? `Restored ${ids.length} shift(s)` : `Deactivated ${ids.length} shift(s)`);
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await refetch();
        } catch (err) {
            starekContext.showError(err);
        }
    };

    const handleToggleActiveSingle = async (shift) => {
        const userId = starekContext?.currentUser?.id ?? starekContext?.user?.id;
        setTogglingActiveId(shift.id);
        try {
            const deactivating = shift.is_active;
            await updateManyShift({
                variables: {
                    data: deactivating ? softDeleteData() : restoreData(),
                    where: { id: { in: [shift.id] } },
                },
            });
            starekContext.showSuccess(
                deactivating
                    ? `${shift.name} is deactivated`
                    : `${shift.name} is restored`,
            );
            await refetch();
        } catch (err) {
            starekContext.showError(err);
        } finally {
            setTogglingActiveId(null);
        }
    };

    const shiftExtendedActions = selectedRows.length > 0 ? [
        {
            label: "Deactivate",
            icon: "fa-ban",
            danger: true,
            disabled: selectedRows.every((r) => !r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => r.is_active), false),
        },
        {
            label: "Restore",
            icon: "fa-undo",
            disabled: selectedRows.every((r) => r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => !r.is_active), true),
        },
    ] : [];

    if (loading) return <EntityLoadingState icon="fa-calendar" />;

    const activeShift = routeEditShift || (isEditMode ? selectedRows[0] : null);

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{
                    singular: UI_STRINGS.ENTITY_SINGULAR,
                    plural: UI_STRINGS.ENTITY_PLURAL,
                }}
                allowSearch={true}
                allowAdd={canWrite}
                allowDelete={canDestroy}
                allowEdit={canWrite}
                allowExtendedActions={canWrite && selectedRows.length > 0}
                extendedActions={shiftExtendedActions}
                allowShowInactive={true}
                showInactive={showInactive}
                onShowInactiveChange={(v) => { setShowInactive(v); setSelectedRows([]); }}
                defaultView={viewMode}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    patchSearchParams({ view: mode });
                    setSelectedRows([]);
                }}
                onDelete={() => handleDelete(selectedRows)}
                onEdit={handleEdit}
            >
                <EntityOperationBar.View
                    mode="timeline"
                    icon="fa fa-calendar"
                    tooltip={UI_STRINGS.VIEW_MODE_TIMELINE}
                >
                    <ShiftsCalendarView
                        items={filteredItems}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        filterText={filterText}
                        onEventClick={(shift) => {
                            navigate(buildListRoute(`/shifts/${shift.id}`, searchParams));
                        }}
                        onToggleActive={handleToggleActiveSingle}
                        togglingActiveId={togglingActiveId}
                    />
                </EntityOperationBar.View>

                <EntityOperationBar.View
                    mode="list"
                    icon="fa fa-list"
                    tooltip={UI_STRINGS.VIEW_MODE_LIST}
                >
                    <ShiftListingView
                        items={filteredItems}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        filterText={filterText}
                        toggleCleared={toggleCleared}
                        noDataComponent={<EntityEmptyState icon="fa-calendar" entityName="Shift" messages={EMPTY_MESSAGES.shifts} isFiltered={!!filterText.trim()} onAdd={handleAdd} />}
                        onRowClicked={handleRowClicked}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            <ShiftSidebar
                show={showAddShiftDrawer}
                onDiscard={handleDrawerClose}
                item={activeShift}
                plantId={starekContext.lastUsedPlant.id}
                onSave={handleSaveShift}
                onDelete={handleDeleteShift}
                onToggleActive={handleToggleActiveSingle}
            />

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting}
                count={selectedRows.length}
                done={deleteDone}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            >
                {confirmCfg.content}
            </ConfirmModal>
        </div>
    );
};

export { Shifts };
